import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { isMac } from '../../global/utils';

/**
 * The key being held that triggers the zoom on our diff stage / canvas.
 */
export const ZOOM_MODIFIER_KEY = isMac ? 'meta' : 'ctrl';

/**
 * The event prop we should check is active while using our event listeners for cursor, and scroll
 * / zoom cancellation.
 */
export const ZOOM_MODIFIER_EVENT_PROP: keyof WheelEvent = isMac ? 'metaKey' : 'ctrlKey';

/**
 * Handles setting up listeners and keybinds for interacting with the stage. Moved to separate hook
 * to keep other file as simple as possible.
 */
export const useStageKeybinds = () => {
  const zoomDirectionRef = useRef<1 | -1>(1);
  const [isZooming, setIsZooming] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [stageStyles, setStageStyles] = useState<CSSProperties>({});
  const [isHoveringShape, setIsHoveringShape] = useState(false);

  // Used to prevent the default action when the zoom key is held down. In Windows 10+, this
  // attempts to zoom the browser which interferes with our functionality. Instead, when the user
  // holds the modifier key we'll add this listener to the document to prevent zoom in / out, then
  // remove it on keyup for scroll / event performance reasons.
  const wheelListener = useCallback((event: WheelEvent) => {
    if (event[ZOOM_MODIFIER_EVENT_PROP]) {
      event.preventDefault();
    }
  }, []);

  const setZoomCallback = useCallback((event: KeyboardEvent) => {
    switch (event.type) {
      case 'keydown':
        document.addEventListener('wheel', wheelListener, { passive: false });
        setIsZooming(true);
        break;
      case 'keyup':
      default:
        document.removeEventListener('wheel', wheelListener);
        setIsZooming(false);
        break;
    }
  }, [wheelListener]);
  useHotkeys(ZOOM_MODIFIER_KEY, setZoomCallback, { keydown: true, keyup: true });

  useEffect(() => {
    if (!isZooming) {
      zoomDirectionRef.current = 1;
    }
  }, [isZooming]);

  let cursor: CSSProperties['cursor'] = 'grab';

  if (isZooming) {
    cursor = zoomDirectionRef.current === 1 ? 'zoom-in' : 'zoom-out';
  } else if (isDragging) {
    cursor = 'grabbing';
  } else if (isHoveringShape) {
    cursor = 'default';
  }

  useEffect(() => {
    setStageStyles({
      cursor,
    });
  }, [cursor]);

  return {
    zoomDirectionRef,
    isDragging,
    setIsDragging,
    setIsHoveringShape,
    stageStyles,
  };
};
